import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** ACH = (Q · 60) / V — cleanroom with FFU ceiling grid, laminar downflow, low-wall returns. */
export function DiagramCleanroomACH() {
  const rx = 60, ry = 70, rw = 230, rh = 140;

  return (
    <DiagramCanvas>
      {/* Isometric back depth lines */}
      <Line x1={rx + rw} y1={ry} x2={rx + rw + 22} y2={ry - 18} stroke={DT.dim} strokeWidth="0.8" strokeDasharray="2,2" />
      <Line x1={rx + rw} y1={ry + rh} x2={rx + rw + 22} y2={ry + rh - 18} stroke={DT.dim} strokeWidth="0.8" strokeDasharray="2,2" />
      <Line x1={rx + rw + 22} y1={ry - 18} x2={rx + rw + 22} y2={ry + rh - 18} stroke={DT.dim} strokeWidth="0.8" strokeDasharray="2,2" />

      {/* Room */}
      <Rect x={rx} y={ry} width={rw} height={rh} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      <SvgText x={rx + rw / 2} y={ry - 6} fill={DT.desc} fontSize="9" fontFamily={DT.mono} textAnchor="middle">Cleanroom</SvgText>

      {/* FFU ceiling grid (4 units) */}
      {[90, 140, 190, 240].map(x => (
        <G key={x}>
          <Rect x={x - 18} y={ry - 14} width={36} height={14} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.2" />
          {[-10, 0, 10].map(dx => (
            <Line key={dx} x1={x + dx} y1={ry - 12} x2={x + dx} y2={ry - 2} stroke={DT.varColor} strokeWidth="0.6" />
          ))}
          <SvgText x={x} y={ry - 4} fill={DT.varColor} fontSize="5" fontFamily={DT.mono} textAnchor="middle">FFU</SvgText>
          {/* Laminar downflow streamlines */}
          {[-10, 0, 10].map(dx => (
            <Line key={`s${dx}`} x1={x + dx} y1={ry} x2={x + dx} y2={ry + rh - 10} stroke={DT.arrow} strokeWidth="0.5" opacity={0.5} strokeDasharray="4,3" />
          ))}
          <Polygon points={`${x - 10},${ry + rh - 10} ${x - 13},${ry + rh - 16} ${x - 7},${ry + rh - 16}`} fill={DT.arrow} opacity={0.6} />
          <Polygon points={`${x},${ry + rh - 10} ${x - 3},${ry + rh - 16} ${x + 3},${ry + rh - 16}`} fill={DT.arrow} opacity={0.6} />
          <Polygon points={`${x + 10},${ry + rh - 10} ${x + 7},${ry + rh - 16} ${x + 13},${ry + rh - 16}`} fill={DT.arrow} opacity={0.6} />
        </G>
      ))}

      {/* Low-wall returns (left & right) */}
      {[ry + rh - 36, ry + rh - 16].map((y, i) => (
        <G key={i}>
          {[0, 1, 2, 3].map(k => (
            <Line key={k} x1={rx + 3 + k * 3} y1={y} x2={rx + 3 + k * 3} y2={y + 12} stroke={DT.stroke} strokeWidth="0.8" />
          ))}
          {[0, 1, 2, 3].map(k => (
            <Line key={`r${k}`} x1={rx + rw - 14 + k * 3} y1={y} x2={rx + rw - 14 + k * 3} y2={y + 12} stroke={DT.stroke} strokeWidth="0.8" />
          ))}
        </G>
      ))}
      <SvgText x={rx + 10} y={ry + rh + 10} fill={DT.desc} fontSize="7" fontFamily={DT.mono}>return</SvgText>

      {/* Height dimension */}
      <Line x1={rx - 14} y1={ry} x2={rx - 14} y2={ry + rh} stroke={DT.dim} strokeWidth="0.8" />
      <Polygon points={`${rx - 14},${ry} ${rx - 18},${ry + 6} ${rx - 10},${ry + 6}`} fill={DT.dim} />
      <Polygon points={`${rx - 14},${ry + rh} ${rx - 18},${ry + rh - 6} ${rx - 10},${ry + rh - 6}`} fill={DT.dim} />
      <SvgText x={rx - 26} y={ry + rh / 2} fill={DT.dim} fontSize="8" fontFamily={DT.mono} textAnchor="middle"
        rotation="-90" originX={rx - 26} originY={ry + rh / 2}>H</SvgText>

      {/* V badge inside room */}
      <Rect x={rx + rw / 2 - 26} y={ry + rh / 2 - 10} width={52} height={20} fill="rgba(0,0,0,0.5)" stroke={DT.varColor} strokeWidth="0.8" />
      <SvgText x={rx + rw / 2} y={ry + rh / 2 + 4} fill={DT.varColor} fontSize="11" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">V (ft³)</SvgText>

      {/* Labels */}
      <LeaderLine x1={165} y1={ry - 14} x2={40} y2={36} varName="Q" desc="total supply CFM" anchor="start" />
      <LeaderLine x1={rx + rw / 2} y1={ry + rh / 2 + 10} x2={20} y2={230} varName="V" desc="room volume" anchor="start" />
      <LeaderLine x1={rx + rw + 12} y1={ry + rh - 30} x2={330} y2={230} varName="ACH" desc="air changes/hr" anchor="end" />

      <FormulaTag formula="ACH = (Q · 60) / V    (ISO 5 ≥ 240 ACH, ISO 7 ≥ 60 ACH)" />
    </DiagramCanvas>
  );
}
